<template>
  <div class="edit-container-goods-main">
    <vxe-grid
      ref="grid"
      size="mini"
      row-id="shipmentContainerGoodsCode"
      border
      resizable
      auto-resize
      show-overflow
      highlight-hover-row
      :data="displayData"
      :columns="tableColumns"
      :height="tableHeight"
      :row-style="rowStyleMethod"
      :cell-class-name="cellClassName"
      :span-method="spanMethod"
      :checkbox-config="{ highlight: true, reserve: true, range: true }"
      :scroll-y="{ enabled: true, gt: 0 }"
      :edit-config="{
        trigger: 'click',
        mode: 'row',
        showStatus: false,
        enabled: !isBooked,
        activeMethod: canEditRow,
      }"
      @edit-actived="onEditActived"
      @edit-closed="onEditClosed"
      @checkbox-change="checkboxChange"
      @checkbox-all="checkboxAll"
      @checkbox-range-change="checkboxChange"
    >
      <template #seqno="{ seq, row }">
        {{ row._seq || seq }}
      </template>

      <template #opt="{ row }">
        <div v-if="row._isGroup">
          <el-button
            type="danger"
            plain
            size="mini"
            class="px-2"
            @click="
              $emit('remove-goods', {
                goodsRows: row.children,
                tip:
                  $l('tips_confirm_group_remove_assignment') ||
                  '确认移除该组分配记录吗？',
              })
            "
          >
            <i class="el-icon-delete"></i>
            {{ $l('btn_group_remove_short') || '整组' }}
          </el-button>
        </div>
        <div
          v-else
          class="edit-container-goods-main_remove-btn"
          :disabled="isBooked"
          @click="$emit('remove-goods', { goodsRow: row })"
        >
          <i class="el-icon-close"></i>
        </div>
      </template>

      <template #containerBoxQty="{ row }">
        <template v-if="row._isGroup">
          <div
            class="edit-container-goods-main__group-content"
            @click="toggleGroup(row._groupKey)"
          >
            <i
              class="el-icon-arrow-right edit-container-goods-main__group-toggle"
              :class="{
                'is-expanded': expandedGroups[row._groupKey] === true,
              }"
            ></i>
            <span class="edit-container-goods-main__group-main">
              {{ row.groupLabel }}
            </span>
            <span class="edit-container-goods-main__group-meta">
              <span class="edit-container-goods-main__group-count">
                {{ row.groupCount }}{{ $l('label_item_count') || '项' }}
              </span>
              <span class="edit-container-goods-main__group-divider">|</span>
              <span
                >{{ $l('table_label_ctnQty') || '箱数' }}
                {{ row.groupBoxQty }}</span
              >
              <span class="edit-container-goods-main__group-divider">|</span>
              <span
                >{{ $l('table_label_goodsQty') || '数量' }}
                {{ row.groupGoodsQty }}</span
              >
              <span class="edit-container-goods-main__group-divider">|</span>
              <span
                >{{ $l('label_volume') || '体积' }}
                {{ row.groupVolume }} m³</span
              >
              <span class="edit-container-goods-main__group-divider">|</span>
              <span
                >{{ $l('label_gross_weight') || '毛重' }}
                {{ row.groupWeight }} kg</span
              >
            </span>
          </div>
        </template>
        <span v-else>{{ row.containerBoxQty }}</span>
      </template>

      <template #amzSkuNo="{ row }">
        <span>{{ row.amzSkuNo || '-' }}</span>
      </template>

      <template #empty>
        <div class="edit-container-goods-main__empty">
          <i class="el-icon-box" style="font-size:24px;color:#cbd5e1;" />
          <span>{{
            $l('tips_click_plus_assign') || '点击左侧产品行的 + 按钮分配产品'
          }}</span>
        </div>
      </template>

      <template #amzSkuNoHeader>
        <filter-content
          :text="$l('table_label_sku') || 'SKU'"
          :items="options.amzSkuNo"
          @filter="(e) => (filters.amzSkuNo = e)"
          submitFilterOnClose
        />
      </template>
      <template #amzStoreSkuNoHeader>
        <filter-content
          :text="$l('table_label_storeSku') || '店铺 SKU'"
          :items="options.amzStoreSkuNo"
          @filter="(e) => (filters.amzStoreSkuNo = e)"
          submitFilterOnClose
        />
      </template>
      <template #goodsNameHeader>
        <filter-content
          :text="$l('table_label_goodsName') || '产品名称'"
          :items="options.goodsName"
          @filter="(e) => (filters.goodsName = e)"
          submitFilterOnClose
        />
      </template>
      <template #planNoHeader>
        <filter-content
          :text="$l('form_label_planNos') || '补货计划编号'"
          :items="options.planNo"
          @filter="(e) => (filters.planNo = e)"
          submitFilterOnClose
        />
      </template>
      <template #fbaDcHeader>
        <filter-content
          :text="$l('table_label_fbaDc') || 'FBA DC'"
          :items="options.fbaDc"
          @filter="(e) => (filters.fbaDc = e)"
          submitFilterOnClose
        />
      </template>
      <template #shippingFactoryNameHeader>
        <filter-content
          :text="$l('table_label_shippingFactoryName') || '送货工厂'"
          :items="options.shippingFactoryName"
          @filter="(e) => (filters.shippingFactoryName = e)"
          submitFilterOnClose
        />
      </template>
    </vxe-grid>
  </div>
</template>

<script>
import lodash from 'lodash'
import TableAutoHeight from '@/mixins/table-auto-height'
import {
  applyTableFilters,
  buildFilterOptions,
  resetTableFilters,
} from '../table-filter-helpers'
import tableColumns from './table-columns'
import {
  buildEditContainerGoodsColumns,
  getEditContainerGoodsGroupMetrics,
  getEditContainerGoodsGroupSpan,
} from './grouping-helpers'
import {
  getCheckedCodeMap,
  getCheckedRows,
  isGroupChecked,
  updateCheckedCodes,
} from '../group-checkbox-helpers'

export default {
  mixins: [TableAutoHeight],
  props: {
    tableData: { type: Array, default: () => [] },
    groupFields: { type: Array, default: () => [] },
    isBooked: { type: Boolean, default: false },
  },
  data() {
    return {
      editSnapshot: {},
      tableColumns: [],
      expandedGroups: {},
      checkedGoodsCodes: [],
      filters: {
        amzSkuNo: [],
        amzStoreSkuNo: [],
        goodsName: [],
        planNo: [],
        fbaDc: [],
        shippingFactoryName: [],
      },
    }
  },
  created() {
    this.refreshBaseColumns()
  },
  mounted() {
    this.$emit('group-state-change', this.hasGroups())
  },
  computed: {
    options() {
      return buildFilterOptions(this.tableData, Object.keys(this.filters))
    },
    filteredRows() {
      return applyTableFilters(this.tableData, this.filters, this.options)
    },
    displayData() {
      if (!this.groupFields.length) return this.filteredRows

      const emptyLabel = this.$l('INDEX.label_empty_value') || '(空)'
      const groups = {}
      const groupOrder = []
      const result = []

      const sortedRows = lodash.orderBy(
        this.filteredRows,
        this.groupFields.map((field) => (row) => {
          const value = row[field]
          return lodash.isUndefined(value) ||
            lodash.isNull(value) ||
            value === ''
            ? ''
            : value
        })
      )

      sortedRows.forEach((row) => {
        const groupValues = this.groupFields.map((field) => {
          const value = row[field]
          return lodash.isUndefined(value) ||
            lodash.isNull(value) ||
            value === ''
            ? null
            : value
        })
        const key = JSON.stringify(groupValues)

        if (!groups[key]) {
          groups[key] = {
            key,
            label: groupValues
              .map((value) => (value === null ? emptyLabel : value))
              .join(' · '),
            rows: [],
          }
          groupOrder.push(key)
        }
        groups[key].rows.push(row)
      })

      groupOrder.forEach((key, index) => {
        const group = groups[key]
        const metrics = getEditContainerGoodsGroupMetrics(group.rows)
        const groupSeq = index + 1
        result.push({
          _isGroup: true,
          shipmentContainerGoodsCode: `group-${index}`,
          _groupKey: key,
          _seq: String(groupSeq),
          groupLabel: group.label,
          children: group.rows,
          ...metrics,
        })
        if (this.expandedGroups[key] === true) {
          group.rows.forEach((r, childIdx) =>
            result.push({ ...r, _seq: `${groupSeq}.${childIdx + 1}` })
          )
        }
      })

      return result
    },
  },
  watch: {
    isBooked() {
      this.refreshBaseColumns()
      this.clearCheckboxRow()
    },
    groupFields(newVal, oldVal) {
      if (!!newVal.length !== !!oldVal.length) {
        this.updateTableColumns()
      }
      this.clearCheckboxRow()
      this.expandedGroups = {}
      this.$emit('expand-state-change', false)
      this.$emit('group-state-change', this.hasGroups())
    },
    tableData() {
      this.resetFilters()
      this.clearCheckboxRow()
      this.expandedGroups = {}
      this.$emit('expand-state-change', false)
      this.$emit('group-state-change', this.hasGroups())
    },
    filters: {
      handler() {
        this.clearCheckboxRow()
        this.expandedGroups = {}
        this.$emit('expand-state-change', false)
        this.$emit('group-state-change', this.hasGroups())
      },
      deep: true,
    },
  },
  methods: {
    resetFilters() {
      this.filters = resetTableFilters(this.filters)
    },
    refreshBaseColumns() {
      this._baseColumns = tableColumns(this)
      this.updateTableColumns()
    },
    updateTableColumns() {
      this.tableColumns = buildEditContainerGoodsColumns(
        this._baseColumns,
        !!this.groupFields.length
      )
    },
    toggleGroup(key) {
      const isExpanded = this.expandedGroups[key] === true
      this.$set(this.expandedGroups, key, !isExpanded)
      this.$nextTick(async () => {
        await this.syncGroupRowsChecked()
        this.$emit('expand-state-change', this.isAllGroupsExpanded())
      })
    },
    expandAllGroups() {
      this.setAllGroupsExpanded(true)
    },
    collapseAllGroups() {
      this.setAllGroupsExpanded(false)
    },
    toggleAllGroups() {
      if (this.isAllGroupsExpanded()) {
        this.collapseAllGroups()
        return
      }

      this.expandAllGroups()
    },
    setAllGroupsExpanded(expanded) {
      const groupKeys = this.getGroupKeys()

      if (!this.groupFields.length || !groupKeys.length) {
        this.expandedGroups = {}
        this.$emit('expand-state-change', false)
        this.$emit('group-state-change', false)
        return
      }

      const nextExpandedGroups = {}
      groupKeys.forEach((key) => {
        nextExpandedGroups[key] = expanded
      })
      this.expandedGroups = nextExpandedGroups

      this.$nextTick(async () => {
        await this.syncGroupRowsChecked()
        this.$emit('expand-state-change', this.isAllGroupsExpanded())
        this.$emit('group-state-change', true)
      })
    },
    getGroupKeys() {
      if (!this.groupFields.length) return []

      return this.displayData
        .filter((row) => row._isGroup && row._groupKey)
        .map((row) => row._groupKey)
    },
    hasGroups() {
      return !!this.getGroupKeys().length
    },
    isAllGroupsExpanded() {
      const groupKeys = this.getGroupKeys()

      return (
        groupKeys.length > 0 &&
        groupKeys.every((key) => this.expandedGroups[key] === true)
      )
    },
    canEditRow({ row }) {
      return !this.isBooked && !row._isGroup
    },
    onEditActived({ row }) {
      if (row?._isGroup) return

      this.editSnapshot = {
        shipmentContainerGoodsCode: row.shipmentContainerGoodsCode,
        containerBoxQty: row.containerBoxQty,
      }
    },
    onEditClosed({ row, column }) {
      if (row?._isGroup) return
      if (column?.property !== 'containerBoxQty') return
      if (
        row.shipmentContainerGoodsCode !==
        this.editSnapshot.shipmentContainerGoodsCode
      )
        return
      if (
        Number(row.containerBoxQty || 0) ===
        Number(this.editSnapshot.containerBoxQty || 0)
      )
        return

      this.$emit('update-goods', { goodsRow: row, value: row.containerBoxQty })
    },
    rowStyleMethod({ row }) {
      if (!row._isGroup) return null

      return {
        backgroundColor: '#f8fafc',
        fontWeight: '600',
      }
    },
    cellClassName({ row, column }) {
      if (!row._isGroup) return ''

      if (column.type === 'seq') return 'is-group-row-cell'
      if (column.property === 'containerBoxQty')
        return 'is-group-row-cell is-group-row-merged'

      return 'is-group-row-cell'
    },
    spanMethod(args) {
      return getEditContainerGoodsGroupSpan({
        ...args,
        columns: this.tableColumns,
      })
    },
    async checkboxChange({ row, checked, records = [], reserves = [] } = {}) {
      const $table = this.$refs.grid
      if (!$table) return

      if (row) {
        this.checkedGoodsCodes = updateCheckedCodes(
          this.checkedGoodsCodes,
          row._isGroup ? row.children : [row],
          'shipmentContainerGoodsCode',
          checked
        )
      } else {
        const rows = records.concat(reserves).reduce((result, item) => {
          return result.concat(item && item._isGroup ? item.children : [item])
        }, [])
        this.checkedGoodsCodes = updateCheckedCodes(
          [],
          rows,
          'shipmentContainerGoodsCode',
          true
        )
      }

      await this.syncGroupRowsChecked()
      this.emitCheckboxChange()
    },
    async checkboxAll({ checked }) {
      const $table = this.$refs.grid
      if (!$table) return

      if (!checked) {
        await this.clearCheckboxRow()
        return
      }

      this.checkedGoodsCodes = updateCheckedCodes(
        [],
        this.filteredRows,
        'shipmentContainerGoodsCode',
        true
      )
      await this.syncGroupRowsChecked()
      this.emitCheckboxChange()
    },
    getCheckedGoodsRows() {
      return getCheckedRows(
        this.filteredRows,
        'shipmentContainerGoodsCode',
        this.checkedGoodsCodes
      )
    },
    async syncGroupRowsChecked() {
      if (!this.groupFields.length || !this.$refs.grid) return

      const checkedCodeMap = getCheckedCodeMap(this.checkedGoodsCodes)
      const goodsRows = this.displayData.filter(
        (row) => !row._isGroup && row.shipmentContainerGoodsCode
      )
      const groupRows = this.displayData.filter((row) => row._isGroup)

      await Promise.all(
        goodsRows.map((row) =>
          this.$refs.grid.setCheckboxRow(
            row,
            !!checkedCodeMap[row.shipmentContainerGoodsCode]
          )
        )
      )
      await Promise.all(
        groupRows.map((row) => {
          return this.$refs.grid.setCheckboxRow(
            row,
            isGroupChecked(row, 'shipmentContainerGoodsCode', checkedCodeMap)
          )
        })
      )
    },
    emitCheckboxChange() {
      this.$emit('checkbox-change', this.getCheckedGoodsRows())
    },
    async clearCheckboxRow() {
      const $table = this.$refs.grid
      this.checkedGoodsCodes = []
      if ($table) {
        await $table.clearCheckboxRow()
        await $table.clearCheckboxReserve()
      }
      this.$emit('checkbox-change', [])
    },
    getHeight() {
      if (!this.tableRef || !this.tableRef.offsetHeight) return

      setTimeout(() => {
        const { bottom = 0 } = this.$el.getBoundingClientRect() || {}
        const { top = 0 } = this.tableRef.getBoundingClientRect() || {}
        this.tableHeight = bottom - top
      }, 200)
    },
  },
}
</script>

<style lang="scss" scoped>
.edit-container-goods-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 0 10px 0;
  background: #fff;
}

.edit-container-goods-main_remove-btn {
  width: 24px;
  height: 24px;
  border-radius: 5px;
  border: 1px solid rgba(245, 108, 108, 0.2);
  background: rgba(245, 108, 108, 0.1);
  color: #f56c6c;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  i {
    line-height: initial;
  }
}

.edit-container-goods-main_remove-btn:hover {
  background: rgba(245, 108, 108, 0.2);
  border-color: rgba(245, 108, 108, 0.35);
}

.edit-container-goods-main__empty {
  display: flex;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: #94a3b8;
  font-size: 12px;
}

.edit-container-goods-main__group-toggle {
  flex-shrink: 0;
  font-size: 12px;
  color: #64748b;
  transition: transform 0.15s;
  &.is-expanded {
    transform: rotate(90deg);
  }
}

.edit-container-goods-main__group-content {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  width: 100%;
  padding-left: 4px;
  text-align: left;
  cursor: pointer;
}

.edit-container-goods-main__group-main {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1e293b;
  font-size: 12px;
  font-weight: 600;
}

.edit-container-goods-main__group-meta {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
  white-space: nowrap;
  color: #64748b;
  font-size: 10px;
  font-weight: 500;
}

.edit-container-goods-main__group-count {
  padding: 0 6px;
  color: #64748b;
  background: #e2e8f0;
  border-radius: 8px;
}

.edit-container-goods-main__group-divider {
  color: #cbd5e1;
}

::v-deep .vxe-body--column.is-group-row-cell {
  background-color: #f8fafc;
}

::v-deep .vxe-body--column.is-group-row-merged .vxe-cell {
  max-height: none !important;
  overflow: visible !important;
  white-space: normal !important;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}
</style>
